import React, { useState, useEffect } from 'react';
import api from '../../services/api';

const IMAGE_BASE_URL = process.env.REACT_APP_IMAGE_BASE_URL || 'http://localhost:1338/';

export default function UserSelector({ onUserSelected }) {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState('');

    useEffect(() => {
        async function discoverUsers() {
            try {
                setLoading(true);
                // GET /channels is public and returns members with full profile
                const res = await api.get('/channels');
                const channels = res?.data || res || [];

                // Aggregate unique users from all channel members
                const userMap = {};
                channels.forEach(ch => {
                    (ch.members || []).forEach(m => {
                        const id = m._id || m.id;
                        if (id && !userMap[id]) {
                            userMap[id] = m;
                        }
                    });
                });

                const uniqueUsers = Object.values(userMap).sort((a, b) =>
                    (a.name || '').localeCompare(b.name || '')
                );

                setUsers(uniqueUsers);
            } catch (e) {
                console.error('Failed to discover users:', e);
                setError('Could not connect to server. Make sure the backend is running.');
            } finally {
                setLoading(false);
            }
        }
        discoverUsers();
    }, []);

    const filtered = users.filter(u =>
        (u.name || '').toLowerCase().includes(search.toLowerCase()) ||
        (u.email || '').toLowerCase().includes(search.toLowerCase())
    );

    const handleSelect = (selectedUser) => {
        // Store selected user in this app's localStorage
        const normalizedUser = {
            ...selectedUser,
            _id: selectedUser._id || selectedUser.id,
            id: selectedUser._id || selectedUser.id,
        };
        localStorage.setItem('user', JSON.stringify(normalizedUser));
        // Note: no token stored, so only public endpoints will work for now
        // Token-protected actions like send message will be handled gracefully
        onUserSelected(normalizedUser);
    };

    const getAvatar = (user) => {
        if (user.profilePhoto) {
            return user.profilePhoto.startsWith('http')
                ? user.profilePhoto
                : `${IMAGE_BASE_URL}${user.profilePhoto}`;
        }
        return `https://ui-avatars.com/api/?name=${encodeURIComponent(user.name || 'User')}&background=6C63FF&color=fff&size=80`;
    };

    return (
        <div
            style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(5,5,10,0.96)',
                backdropFilter: 'blur(20px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 9999,
                fontFamily: "'Inter', system-ui, sans-serif",
            }}
        >
            <div
                style={{
                    background: 'linear-gradient(145deg, #1a1a2e, #16213e)',
                    border: '1px solid rgba(108,99,255,0.3)',
                    borderRadius: '24px',
                    padding: '40px',
                    width: '100%',
                    maxWidth: '480px',
                    boxShadow: '0 25px 80px rgba(108,99,255,0.15)',
                }}
            >
                {/* Header */}
                <div style={{ textAlign: 'center', marginBottom: '32px' }}>
                    <div style={{
                        width: '60px',
                        height: '60px',
                        borderRadius: '18px',
                        background: 'linear-gradient(135deg, #6C63FF, #4ecdc4)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '28px',
                        margin: '0 auto 16px',
                        boxShadow: '0 8px 24px rgba(108,99,255,0.4)'
                    }}>
                        💬
                    </div>
                    <h1 style={{ color: '#fff', fontSize: '22px', fontWeight: 700, margin: 0 }}>
                        Who are you?
                    </h1>
                    <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '14px', marginTop: '8px' }}>
                        Select your account to continue
                    </p>
                </div>

                {/* Search */}
                {users.length > 5 && (
                    <input
                        type="text"
                        placeholder="Search your name..."
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        style={{
                            width: '100%',
                            background: 'rgba(255,255,255,0.05)',
                            border: '1px solid rgba(255,255,255,0.1)',
                            borderRadius: '12px',
                            padding: '10px 16px',
                            color: '#fff',
                            fontSize: '14px',
                            outline: 'none',
                            marginBottom: '20px',
                            boxSizing: 'border-box',
                        }}
                    />
                )}

                {/* User List */}
                {loading && (
                    <div style={{ textAlign: 'center', color: 'rgba(255,255,255,0.4)', padding: '40px 0' }}>
                        <div style={{
                            width: '32px',
                            height: '32px',
                            border: '3px solid rgba(108,99,255,0.3)',
                            borderTop: '3px solid #6C63FF',
                            borderRadius: '50%',
                            animation: 'spin 0.8s linear infinite',
                            margin: '0 auto 12px',
                        }} />
                        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                        Loading workspace members...
                    </div>
                )}

                {error && (
                    <div style={{
                        background: 'rgba(239,68,68,0.1)',
                        border: '1px solid rgba(239,68,68,0.3)',
                        borderRadius: '12px',
                        padding: '16px',
                        color: '#f87171',
                        fontSize: '14px',
                        textAlign: 'center',
                    }}>
                        {error}
                    </div>
                )}

                {!loading && !error && (
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px',
                        maxHeight: '320px',
                        overflowY: 'auto',
                        paddingRight: '4px',
                    }}>
                        {filtered.length === 0 && (
                            <div style={{ textAlign: 'center', color: 'rgba(255,255,255,0.3)', padding: '32px 0' }}>
                                No members found
                            </div>
                        )}
                        {filtered.map(user => (
                            <button
                                key={user._id || user.id}
                                onClick={() => handleSelect(user)}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '12px',
                                    background: 'rgba(255,255,255,0.04)',
                                    border: '1px solid rgba(255,255,255,0.06)',
                                    borderRadius: '14px',
                                    padding: '12px 16px',
                                    cursor: 'pointer',
                                    transition: 'all 0.18s ease',
                                    width: '100%',
                                    textAlign: 'left',
                                    color: '#fff',
                                }}
                                onMouseEnter={e => {
                                    e.currentTarget.style.background = 'rgba(108,99,255,0.15)';
                                    e.currentTarget.style.borderColor = 'rgba(108,99,255,0.4)';
                                    e.currentTarget.style.transform = 'translateX(4px)';
                                }}
                                onMouseLeave={e => {
                                    e.currentTarget.style.background = 'rgba(255,255,255,0.04)';
                                    e.currentTarget.style.borderColor = 'rgba(255,255,255,0.06)';
                                    e.currentTarget.style.transform = 'translateX(0)';
                                }}
                            >
                                <img
                                    src={getAvatar(user)}
                                    alt={user.name}
                                    style={{
                                        width: '40px',
                                        height: '40px',
                                        borderRadius: '12px',
                                        objectFit: 'cover',
                                        flexShrink: 0,
                                    }}
                                    onError={e => {
                                        e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(user.name || 'U')}&background=6C63FF&color=fff`;
                                    }}
                                />
                                <div style={{ overflow: 'hidden' }}>
                                    <div style={{ fontWeight: 600, fontSize: '15px', color: '#fff' }}>
                                        {user.name}
                                    </div>
                                    <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.4)', marginTop: '2px' }}>
                                        {user.email || user.username || 'Team Member'}
                                    </div>
                                </div>
                                <div style={{ marginLeft: 'auto', color: 'rgba(108,99,255,0.7)', fontSize: '18px' }}>
                                    →
                                </div>
                            </button>
                        ))}
                    </div>
                )}

                <p style={{
                    color: 'rgba(255,255,255,0.2)',
                    fontSize: '11px',
                    textAlign: 'center',
                    marginTop: '24px',
                    marginBottom: 0,
                }}>
                    Your selection is saved. You won't see this again.
                </p>
            </div>
        </div>
    );
}
